import h, { FunctionComponent } from 'preact';
import { useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { usePopper } from 'react-popper';
import classNames from 'classnames';

/**
 * Icon that shows a tooltip on hover/focus
 *
 * Needs to use a portal to manage the tooltip content as the basket sidebar is within an overflowed container div
 * and you can't "punch out" of that overflow. So the tooltips need to live outside the overflowed container, see
 * resources/views/products/view.twig for div#js-tooltips
 *
 * @param type "error" or "warning"
 * @param message The tooltip message to show
 * @constructor
 */
const MessageIcon: FunctionComponent<MessageIconProps> = ({ type, message }) => {
    const [ referenceElem, setReferenceElem ] = useState<HTMLButtonElement|null>(null);
    const [ popperElem, setPopperElem ] = useState<HTMLDivElement|null>(null);
    const [ arrowElem, setArrowElem ] = useState<HTMLDivElement|null>(null);
    const { styles, attributes, update } = usePopper(referenceElem, popperElem, {
        placement: 'left',
        modifiers: [{ name: 'arrow', options: { element: arrowElem }}],
    });

    const onEnter = () => {
        popperElem?.setAttribute('data-show', '');
        if (update !== null) {
            update();
        }
    };

    const onLeave = () => {
        popperElem?.removeAttribute('data-show');
        if (update !== null) {
            update();
        }
    };

    const icon = (type === 'error') ? '⚠' : 'ⓘ';

    return <>
        <button
            type="button"
            class={classNames('js-icon-' + type, { 'text-danger': type === 'error', 'text-mid-grey': type === 'warning' })}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            onFocus={onEnter}
            onBlur={onLeave}
            ref={setReferenceElem}
        >
            {icon}
        </button>
        {createPortal(<div ref={setPopperElem} className="js-tooltip" style={styles.popper} {...attributes.popper}>
            {message}
            <div ref={setArrowElem} style={styles.arrow} class="js-arrow" />
        </div>, document.getElementById('js-tooltips') as HTMLDivElement)}
    </>;
}

interface MessageIconProps {
    type: MessageType;
    message: string;
}

type MessageType = 'error' | 'warning';

export default MessageIcon;
